// @flow
import * as React from 'react';
import { useMutation } from 'urql';
import { featureIsEnabled } from '@ta/common.features';
import LogImpressions from './LogImpressions.gql';
import { TripTrackingImpression, TrackingImpressionContextProvider } from './ImpressionContext';
import type { ImpressionAttributes, ImpressionSources } from './ImpressionAttributes';

/**
 * We want the impression action to run on mount, not render.
 * This component will run the method when it first mounts so that we only
 * get one impression. We make sure that parent impressions complete before
 * logging child impressions
 */

type TripImpressionProps = {|
  /** The impression source. */
  source: ImpressionSources,
  /** An object of literal attributes to send along with the impression */
  attributes: ImpressionAttributes,
  /** Child nodes to render */
  children: React.Node,
|};

const TrackImpression = ({ source, attributes, children }: TripImpressionProps) => {
  const impressionContext = React.useContext(TripTrackingImpression);
  const [id, setId] = React.useState(undefined);
  const [isImpressionLogged, setImpressionLogged] = React.useState(false);
  // if there's a parent impression tracker, wait for it
  const hasParentImpression = Object.keys(impressionContext).length > 0;
  const isParentComplete = !hasParentImpression || impressionContext.isImpressionLogged;
  const [, executeImpression] = useMutation(LogImpressions);
  const parentId = impressionContext.impressionId || null;
  const sourceAttributes = attributes[source];

  // This is relatively shallow and guaranteed to be serializable, so it should be safe to use the string representation as the key
  const key = JSON.stringify(attributes);
  const impressionInput = React.useMemo(
    () =>
      ({
        [source]: attributes[source],
        parentId,
      }: any),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, parentId, key],
  );

  React.useEffect(() => {
    // Wait for any parent impressions to complete first
    if (isParentComplete) {
      setImpressionLogged(false);
      executeImpression({ impressions: [impressionInput] }).then(({ data }) => {
        if (!data) {
          return;
        }
        const impressionId = data && data.logImpressions && data.logImpressions[0];
        if (impressionId) {
          setId(impressionId);
          setImpressionLogged(true);
        }
      });
    }
  }, [isParentComplete, impressionInput, executeImpression]);

  if (!featureIsEnabled('react_tracking_impressions')) {
    return children;
  }

  if (!sourceAttributes) {
    console.warn(`Could not find attributes for ${source}`);
    return children;
  }

  return (
    <TrackingImpressionContextProvider impressionId={id} isImpressionLogged={isImpressionLogged}>
      {children}
    </TrackingImpressionContextProvider>
  );
};

export default TrackImpression;
